import { defineStore } from "pinia";
import { useGrimoireStore } from "./useGrimoireStore";
import { useLocaleStore } from "./useLocaleStore";
import { usePlayersStore } from "./usePlayersStore";
import { useUserPreferencesStore } from "./useUserPreferencesStore";
import { useVotingStore } from "./useVotingStore";
import { useDialogStore } from "./useDialogStore";
import socket from "@/services/socket";
import type { Persist } from "@/types";

interface SessionState {
  password: string|undefined;
  sessionId: string;
  isPlayerOrSpectator: boolean;
  isReconnecting: boolean;
  playerCount: number;
  ping: number;
  playerId: string;
  claimedSeat: number;
  isRolesDistributed: boolean;
}

export const useSessionStore = defineStore("session", {
  state: (): SessionState => ({
    password: undefined,
    sessionId: "",
    isPlayerOrSpectator: false,
    isReconnecting: false,
    playerCount: 0,
    ping: 0,
    playerId: "",
    claimedSeat: -1,
    isRolesDistributed: false,
  }),
  actions: {
    setPlayerId(playerId: string) {
      this.playerId = playerId;
    },
    setReconnecting(isReconnecting: boolean) {
      this.isReconnecting = isReconnecting;
    },
    setPlayerCount(playerCount: number) {
      this.playerCount = playerCount;
    },
    setPing(ping: number) {
      this.ping = ping;
    },
    claimSeat(claimedSeat: number) {
      this.claimedSeat = claimedSeat;
      const playersStore = usePlayersStore();
      const userPreferences = useUserPreferencesStore();
      if (
        this.isPlayerOrSpectator &&
        playersStore.players.length > claimedSeat &&
        (claimedSeat < 0 || !playersStore.players[claimedSeat]?.id)
      ) {
        userPreferences.discordName = playersStore.players[claimedSeat]?.discordTag || userPreferences.discordName;
        socket.send("claim", [claimedSeat, this.playerId]);
      }
    },

    setSessionId(sessionId: string) {
      this.sessionId = sessionId
        .toLocaleLowerCase()
        .replaceAll(/[^0-9a-z]/g, "");
    },

    // Session actions
    async hostSession() {
      if (this.sessionId) return;
      const grimoire = useGrimoireStore();
      const localeStore = useLocaleStore();
      const userPreferences = useUserPreferencesStore();
      const dialogStore = useDialogStore();
      const t = localeStore.t;
      const sessionId = await dialogStore.prompt(
        t("prompt.createSession"),
        String(Math.round(Math.random() * 10000)),
      );
      const password = await dialogStore.prompt(
        t("prompt.sessionPassword")
      );
      if (!sessionId || !password) return;
      const votingStore = useVotingStore();
      votingStore.clearVoteHistory();
      this.isPlayerOrSpectator = false;
      this.password = password;
      this.setSessionId(sessionId);
      grimoire.setGamePhase("pregame");
      grimoire.setDayCount(0);
      userPreferences.hideGrim = false;
      const link = globalThis.location.href + sessionId;
      document
        .querySelector('meta[property="og:url"]')
        ?.setAttribute("content", link);
    },

    async joinSession() {
      if (this.sessionId) return this.leaveSession();
      const localeStore = useLocaleStore();
      const userPreferences = useUserPreferencesStore();
      const dialogStore = useDialogStore();
      const t = localeStore.t;
      let sessionId = await dialogStore.prompt(t("prompt.joinSession"));
      if (sessionId?.match(/^https?:\/\//i)) {
        sessionId = sessionId.split("/").pop() || null;
      }
      if (sessionId) {
        const votingStore = useVotingStore();
        votingStore.clearVoteHistory();
        this.isPlayerOrSpectator = true;
        userPreferences.hideGrim = false;
        this.setSessionId(sessionId);
        document
          .querySelector('meta[property="og:url"]')
          ?.setAttribute("content", globalThis.location.href);
      }
    },

    async leaveSession() {
      const localeStore = useLocaleStore();
      const grimoireStore = useGrimoireStore();
      const dialogStore = useDialogStore();
      const t = localeStore.t;
      if (await dialogStore.confirm(t("prompt.leaveSession"))) {
        this.claimSeat(-1);
        this.setSessionId("");
        grimoireStore.setGamePhase("pregame");
        this.isPlayerOrSpectator = false;
        globalThis.location.replace("/");
      }
    },

    copySessionUrl() {
      const link = globalThis.location.href + this.sessionId;
      navigator.clipboard.writeText(link);
    },
    initializeFromUrl() {
      const hashSessionId = globalThis.location.href.split("/").pop();
      if (
        hashSessionId &&
        hashSessionId !== this.sessionId &&
        hashSessionId !== "townsquare.online"
      ) {
        this.setSessionId(hashSessionId);
        this.isPlayerOrSpectator = true;
      }
    },
  },
  getters: {
    currentPlayerName(): string {
      const locale = useLocaleStore();
      const playersStore = usePlayersStore();
      const t = locale.t;
      if (!this.isPlayerOrSpectator) return t('chat.host');
      return playersStore.players[playersStore.currentPlayerIndex]?.name || "_YOU_";
    },
    isSeated(): boolean {
      const playersStore = usePlayersStore();
      return (!!this.sessionId && !!this.isPlayerOrSpectator && !!playersStore.getById(this.playerId));
    }
  },
  persist: [
    {
      key: "playerId",
      pick: ["playerId"],
    },
    {
      key: "session",
      pick: ["isPlayerOrSpectator", "sessionId", "password"],
      serializer: {
        serialize: (state: SessionState) => {
          return JSON.stringify([state.isPlayerOrSpectator, state.sessionId, state.password]);
        },
        deserialize: (value: string) => {
          const data = JSON.parse(value);
          if (Array.isArray(data) && data.length >= 2) {
            return {
              isPlayerOrSpectator: data[0],
              sessionId: data[1],
              password: data[2] || undefined,
            };
          }
          return {
            isPlayerOrSpectator: false,
            sessionId: "",
            password: undefined,
          };
        },
      },
    },
  ] as Persist<SessionState>,
});
